import { states } from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { CreateAccountFormFields } from '@bulknutrients/bulk-shared-lib/interfaces/IForms';
import { getErrorMessage } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { SkeletonText } from '@components/Placeholders/Skeleton';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useSignup } from '@hooks/customer/useSignup';
import { useKlaviyo } from '@hooks/useKlaviyo';
import { log } from '@lib/logs';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useRouter } from 'next/router';
import { type ReactElement, type ReactNode, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, type UseFormReturn, useForm } from 'react-hook-form';
import { Button, ButtonType } from '../Button';
import { GoogleAutocomplete, Input, Select } from '../FormComponents';

const ADDRESS_FORM_FIELDS = ['address1', 'suburb', 'state', 'postcode'];

function SelectedShippingAddress({
  rhfMethods,
  validAddress,
}: {
  rhfMethods: any;
  validAddress: boolean;
}) {
  if (validAddress) {
    return (
      <div className="border border-solid border-orange bg-orange-20 px-2 py-1 font-semibold dark:bg-orange-dark">
        Selected address:{' '}
        {ADDRESS_FORM_FIELDS.map((field) => rhfMethods.watch(field)).join(', ')}
      </div>
    );
  }
  return null;
}

export function ConfirmManualAddressAlert({
  showFields,
}: {
  showFields: () => void;
}) {
  return (
    <AlertDialog.Root>
      <AlertDialog.Trigger asChild>
        <Button
          id="autocomplete-manual-address"
          buttonStyle="text"
          className="ml-2 my-2"
        >
          Enter address manually
        </Button>
      </AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-[0] animate-[fade-in_150ms_cubic-bezier(0.16,1,0.3,1)] bg-black" />
        <AlertDialog.Content
          className="fixed left-1/2 
            top-1/2 max-h-[85vh] w-[90vw] max-w-[500px] -translate-x-2/4
            -translate-y-2/4 animate-[contentShow_150ms_cubic-bezier(0.16,1,0.3,1)] rounded-3xl bg-white
            p-6 focus:outline-hidden dark:bg-grey-dark"
        >
          <AlertDialog.Title className="m-0 text-center text-xl">
            Are you sure?
          </AlertDialog.Title>
          <AlertDialog.Description className="mb-5 text-base">
            Most customers can find their address using the autocomplete
            functionality which significantly speeds up dispatch and delivery.
            We suggest giving it another try to see if your address is listed.
            <br />
            <br />
            If you can't find your address using the autocomplete, please double
            check your manually entered address. Incorrect addresses will lead
            to delays in receiving your order.
          </AlertDialog.Description>
          <div className="flex justify-center gap-2">
            <AlertDialog.Action asChild>
              <button
                type="button"
                className="rounded-3xl border-none bg-grey-dark px-4 py-2 text-white dark:bg-black"
                onClick={showFields}
              >
                Enter Manually
              </button>
            </AlertDialog.Action>
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="rounded-3xl border-none bg-green px-4 py-2 text-white"
              >
                Try Again
              </button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export function CreateAccountFields({
  methods,
  includeAddress = false,
}: {
  methods: UseFormReturn<CreateAccountFormFields>;
  includeAddress?: boolean;
}) {
  const [showAddressFields, setShowAddressFields] = useState(false);

  const validAddress = ADDRESS_FORM_FIELDS.map((field) =>
    methods.watch(field as keyof CreateAccountFormFields)
  ).every((f) => (f as string) && (f as string).length > 0);

  const showFields = () => setShowAddressFields(true);
  const hideFields = () => setShowAddressFields(false);

  const phoneNumberValidator = async (phone: string) => {
    // Dynamically load libphonenumber-js
    const { isValidPhoneNumber, parsePhoneNumberWithError, ParseError } =
      await import('libphonenumber-js/max');

    const formatPhoneNumber = (value: string): string => {
      try {
        const phoneNumber = parsePhoneNumberWithError(value, 'AU');

        if (phoneNumber) {
          return phoneNumber.format('E.164');
        }
      } catch (error) {
        if (error instanceof ParseError) {
          // Not a phone number, non-existent country, etc.
          log({ error, location: 'AccountSettingsForm/phoneNumberValidator' });
        }
      }

      return value;
    };

    if (phone && !isValidPhoneNumber(phone, 'AU')) {
      return 'Phone number must be valid format';
    }

    methods.setValue('phone', formatPhoneNumber(phone));
  };

  return (
    <BulkErrorBoundary location="Create Account Fields">
      <Input
        label="Email Address"
        name="email"
        id="NewAccountEmail"
        type="email"
        required
        autoComplete="email"
      />
      <Input
        label="First Name"
        name="firstName"
        id="NewAccountFirstName"
        type="text"
        required
        autoComplete="given-name"
      />
      <Input
        label="Last Name"
        name="lastName"
        id="NewAccountLastName"
        type="text"
        required
        autoComplete="family-name"
      />
      <Input
        label="Phone Number"
        name="phone"
        required
        type="tel"
        description="Standard international format (eg +61400123456)"
        validate={{
          validPhone: (value: string) => phoneNumberValidator(value),
        }}
      />
      {includeAddress && (
        <>
          {showAddressFields ? (
            <Button
              onClick={() => {
                hideFields();
                methods.resetField('address1');
                methods.resetField('address2');
                methods.resetField('suburb');
                methods.resetField('postcode');
                methods.resetField('state');
                methods.resetField('country');
                methods.resetField('addressAutocomplete');
                methods.resetField('barcode4State');
              }}
            >
              Reset Address
            </Button>
          ) : (
            <>
              {/* 
                As of 22/05/25, address is only used in the free sample form which requires all addresses to be australia only. 
                So this component now assumes the only people accessing this will be in Australia.
                If this changes, we will need to toggle the use of the Google autocomplete componenet and this Auspost addresser component.
              */}
              {/* <AddresserAutocomplete /> */}
              <GoogleAutocomplete ausOnly />
              <ConfirmManualAddressAlert showFields={showFields} />
              <SelectedShippingAddress
                validAddress={validAddress}
                rhfMethods={methods}
              />
            </>
          )}
          <div
            data-shown={showAddressFields}
            className="hidden grid-cols-1 gap-5 data-[shown=true]:grid"
          >
            <Input
              label="Address Line 1"
              name="address1"
              id="NewAccountAddressLine1"
              type="text"
              required
              autoComplete="address-line1"
            />
            <Input
              label="Address Line 2"
              name="address2"
              id="NewAccountAddressLine2"
              type="text"
              autoComplete="address-line2"
            />
            <Input
              label="Suburb/City"
              name="suburb"
              type="text"
              id="NewAccountSuburb"
              required
              autoComplete="address-level2"
            />
            <Select
              label="Country"
              name="country"
              id="NewAccountCountry"
              options={{
                value: 'AU',
                label: 'Australia',
              }}
              required
            />
            <Input
              label="Postcode"
              name="postcode"
              id="NewAccountPostcode"
              type="text"
              inputMode="numeric"
              required
              autoComplete="postal-code"
              minLength={4}
              maxLength={4}
            />
            {methods.watch('country') === 'AU' && (
              <Select
                label="State"
                name="state"
                id="NewAccountState"
                options={Object.keys(states).map((key) => ({
                  value: key,
                  label: states[key],
                }))}
                required
              />
            )}
            {methods.watch('country') !== 'AU' && (
              <Input
                label="State"
                name="state"
                id="NewAccountState"
                type="text"
                autoComplete="address-level1"
              />
            )}
            <input type="hidden" {...methods.register('barcode4State')} />
          </div>
        </>
      )}
    </BulkErrorBoundary>
  );
}

interface Props {
  referredByCode?: string;
  ambassadorReferreredByCode?: string;
}

export function SignupErrorMessage({ msg }: { msg: string }) {
  return (
    <div>
      <Paragraph>{msg}</Paragraph>
      <Paragraph>
        If you continue to experience issues, please reach out to our{' '}
        <TextLink href="/contact-us">customer service team</TextLink> to get you
        signed up.
      </Paragraph>
    </div>
  );
}

export function KlaviyoErrorMessage() {
  return (
    <Paragraph>
      Failed to subscribe to newsletter or sms list. Your account has been
      created though! You can opt into emails and text messages from your{' '}
      <TextLink href="/account/settings">account settings</TextLink>, or reach
      out to our <TextLink href="/contact-us">customer service team</TextLink>{' '}
      to get you signed up.
    </Paragraph>
  );
}

export function CreateAccountForm({
  referredByCode,
  ambassadorReferreredByCode,
}: Props): ReactElement {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const signup = useSignup();
  const router = useRouter();

  const { newsletterSubscribe, smsSubscribe } = useKlaviyo();

  const rhfMethods = useForm<CreateAccountFormFields>({
    mode: 'onTouched',
    defaultValues: {
      country: 'AU',
    },
  });

  const { formState } = rhfMethods;

  const recaptchaCheck = async () => {
    const recaptchaValue = recaptchaRef?.current?.getValue();
    if (!recaptchaValue) return false;

    const captchaCheckResponse = await fetch('/api/recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ code: recaptchaValue as string, version: 'v2' }),
    });

    const captchaCheck = await captchaCheckResponse.json();

    if (isErrorResponse(captchaCheck)) {
      return false;
    }

    return captchaCheck.data;
  };

  const handleSignup = async (formData: CreateAccountFormFields) => {
    // scroll to the top because a spinner replaces form causing page height
    // to change and the user location so we want that to be obvious to them
    window.scrollTo(0, 0);
    setSubmitting(true);

    // Check the Captcha is correct
    const captchaCheck = await recaptchaCheck();
    if (!captchaCheck) {
      setError(true);
      setErrorMessage('Please complete the captcha correctly');
      setSubmitting(false);
      return;
    }

    // Attempt to create the account, only include referral code if it exists
    let customerAttributes;

    try {
      const customer = await signup({
        email: formData.email,
        firstName: formData.firstName,
        phone: formData.phone,
        lastName: formData.lastName,
        password: formData.password,
        attributes: customerAttributes,
        referredByCode,
        ambassadorReferreredByCode,
      });

      if (isErrorResponse(customer)) {
        throw Error(customer.error.message.join('. '));
      }

      // Klaviyo Subscriptions
      const klaviyoPromises = [] as Array<Promise<boolean>>;
      if (formData.newsletter) {
        // If the customer has signed up for the newsletter then we can add them to Klaviyo
        klaviyoPromises.push(
          newsletterSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
        klaviyoPromises.push(
          smsSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
      }
      const klaviyoSubscribeResponses = await Promise.all(klaviyoPromises);
      if (klaviyoSubscribeResponses.some((response) => !response)) {
        setErrorMessage(<KlaviyoErrorMessage />);
        setError(true);
      }
    } catch (err) {
      log({ error: err });
      const errorMessage = getErrorMessage(err);
      setErrorMessage(<SignupErrorMessage msg={errorMessage} />);
      setError(true);
      setSubmitting(false);
      recaptchaRef.current?.reset();
      return;
    }

    // Got to here we succeeded
    router.push('/account/home');
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      {submitting ? (
        <div className="mb-6 mt-4">
          <SkeletonText heightPixels={700} />
        </div>
      ) : (
        <div>
          <FormProvider {...rhfMethods}>
            <form
              onSubmit={rhfMethods.handleSubmit(handleSignup)}
              className="grid grid-cols-1 gap-5"
            >
              <h4>Your details</h4>

              <CreateAccountFields methods={rhfMethods} />

              <HorizontalDivider />
              <h4>Password</h4>
              <Input
                label="Password"
                name="password"
                type="password"
                description="Passwords must be at least 12 characters long, with an uppercase letter, a lowercase letter, a number, and optionally any of these special characters: @$!%*#?&()"
                required
                autoComplete="new-password"
              />
              <Input
                label="Confirm Password"
                name="confirm"
                type="password"
                required
                autoComplete="new-password"
                validate={(pwdValue: string) => {
                  if (pwdValue !== rhfMethods.getValues('password'))
                    return 'Passwords do not match.';
                }}
              />
              <HorizontalDivider />
              <h4>Bulk Nutrients Newsletter and Offers</h4>
              <Input
                type="checkbox"
                label="Send me exclusive news & offers"
                name="newsletter"
                value="newsletter"
                description={
                  <>
                    I understand that by checking this box, I consent to receive
                    email and sms communications from Bulk Nutrients and agree
                    to the{' '}
                    <TextLink
                      href="/bulk-nutrients-terms-conditions"
                      target="_blank"
                    >
                      T&Cs
                    </TextLink>{' '}
                    and{' '}
                    <TextLink
                      href="/bulk-nutrients-privacy-policy"
                      target="_blank"
                    >
                      Privacy Policy
                    </TextLink>
                    .
                  </>
                }
              />

              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
              />

              <div className="btn-create-account">
                <Button
                  type={ButtonType.submit}
                  disabled={submitting || !formState.isValid}
                  buttonStyle="primary"
                >
                  Create Account
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}
    </>
  );
}
